/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
// import clsx from 'clsx';
import get from 'lodash/get';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost } from 'office-ui-fabric-react';
import startCase from 'lodash/startCase';
import Link from '../Link';
import TextOverflow from '../TextOverflow';
import DetailsList from '../DetailsList';
import styles from './DimensionListItems.style';
import { formatDateString } from '../../utils';

const DimensionListItems = ({
  items,
  associatedLists,
  listType,
  isAssociatedContracts,
  ...other
}) => {
  // Referenced list items that are related to the child of the selected dimension list (child list items)
  const referencedListItems = items.filter(item => {
    return item.isList;
  });

  // Items that are related to the current selected dimension list (parent list items)
  const listItems = items.filter(item => {
    return !item.isList;
  });

  const isAssociatedLists = associatedLists.length > 0;

  const showHierarchy = !!'product, activity-type, channel, geography'.includes(listType);

  const columns = {
    icon: {
      key: 'icon',
      name: 'Lists',
      arianame: 'icon',
      fieldName: 'icon',
      minWidth: 30,
      maxWidth: 30,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon
              iconName="BulletedList"
              ariaLabel="BulletedList"
              className={styles.detailRowIcon}
            />
          </div>
        );
      },
    },
    list: {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      styles: { root: { visibility: 'hidden' } },
      columnActionsMode: 0,
      minWidth: 320,
      maxWidth: 320,
      onRender: item => {
        return (
          <div className={styles.statement}>
            {get(item, 'name')?.length > 50
              ? `${get(item, 'name')?.substr(0, 49)}...`
              : get(item, 'name')}
          </div>
        );
      },
    },
    viewDimensionList: {
      key: 'viewDimensionList',
      name: 'View',
      styles: { root: { visibility: 'hidden' } },
      fieldName: 'viewDimensionList',
      columnActionsMode: 0,
      minWidth: 50,
      onRender: item => {
        const dimensionListId = isAssociatedLists ? get(item, 'listId') : get(item, 'id');
        const dimensionListType = isAssociatedLists ? get(item, 'listType', listType) : listType;
        return (
          <div style={{ width: '85%' }}>
            <TooltipHost
              content="View Dimension List"
              calloutProps={{
                gapSpace: 5,
              }}
            >
              <Link
                target="_blank"
                className={styles.viewLink}
                href={`/dimensionLists/${dimensionListId}/${dimensionListType}`}
              >
                View
              </Link>
            </TooltipHost>
          </div>
        );
      },
    },
    item: {
      key: 'name',
      name: 'Items',
      fieldName: 'name',
      columnActionsMode: 0,
      minWidth: 675,
      maxWidth: 675,
      onRender: item => {
        return <div className={styles.listData}>{get(item, 'name')}</div>;
      },
    },
    hierarchy: {
      key: 'hierarchy',
      name: 'Hierarchy',
      fieldName: 'hierarchy',
      columnActionsMode: 0,
      minWidth: 100,
      maxWidth: 320,
      onRender: item => {
        return <div className={styles.listData}>{get(item, 'hierarchy')}</div>;
      },
    },
    listName: {
      key: 'name',
      name: 'List Name',
      styles: { root: { visibility: 'hidden' } },
      fieldName: 'listName',
      columnActionsMode: 0,
      minWidth: 320,
      maxWidth: 320,
      onRender: item => {
        return <div className={styles.listData}>{get(item, 'listName')}</div>;
      },
    },
    associatedDate: {
      key: 'associatedDate',
      name: 'Date Associated',
      columnActionsMode: 0,
      minWidth: 320,
      onRender: item => {
        return (
          <div className={styles.listData}>
            {formatDateString(item.metadata.lastUpdatedTimestamp)}
          </div>
        );
      },
    },
  };

  const referencedListColumns = [columns.icon, columns.list, columns.viewDimensionList];

  const includedItemColumns = [columns.item];
  if (showHierarchy) includedItemColumns.push(columns.hierarchy);

  const associatedListColumns = [
    columns.icon,
    columns.listName,
    columns.associatedDate,
    columns.viewDimensionList,
  ];

  const contractColumns = {
    icon: {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      arianame: 'icon',
      iconName: 'KnowledgeArticle',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: item => {
        return (
          <TooltipHost
            content={startCase(get(item, 'approvalStatus'))}
            calloutProps={{
              gapSpace: 0,
              target: `#${`statusIcon${get(item, 'contractId')}`}`,
            }}
          >
            <div className={styles.associatedContractStatusIcon}>
              <FontIcon
                iconName="LocationDot"
                ariaLabel="TextDocument"
                id={`statusIcon${get(item, 'contractId')}`}
              />
            </div>
          </TooltipHost>
        );
      },
    },
    contractName: {
      key: 'name',
      name: 'Contract Name',
      fieldName: 'contractName',
      columnActionsMode: 0,
      minWidth: 325,
      maxWidth: 325,
      onRender: item => {
        return <TextOverflow id={item.key} content={get(item, 'contractName')} />;
      },
    },
    contractId: {
      key: 'cId',
      name: 'Contract ID',
      fieldName: 'contractId',
      columnActionsMode: 0,
      minWidth: 320,
      maxWidth: 320,
      onRender: item => {
        return <div className={styles.listData}>{get(item, 'contractId')}</div>;
      },
    },
    partnerName: {
      key: 'pName',
      name: 'Partner Name',
      fieldName: 'partnerName',
      columnActionsMode: 0,
      onRender: item => {
        return <TextOverflow id={item.key} content={get(item, 'partnerName')} />;
      },
    },
  };

  const associatedContractColumns = [
    contractColumns.icon,
    contractColumns.contractName,
    contractColumns.contractId,
    contractColumns.partnerName,
  ];

  return (
    <>
      {!isAssociatedContracts && (
        <>
          {!isAssociatedLists && referencedListItems.length > 0 && (
            <DetailsList
              items={referencedListItems.map(item => ({
                ...item,
                key: item.id,
              }))}
              columns={referencedListColumns}
              {...other}
            />
          )}

          {!isAssociatedLists && listItems.length > 0 && (
            <DetailsList
              items={listItems.map(item => ({
                ...item,
                key: item.id,
              }))}
              columns={includedItemColumns}
              {...other}
            />
          )}

          {isAssociatedLists && associatedLists.length > 0 && (
            <DetailsList
              items={associatedLists.map(item => ({
                ...item,
                key: item.id,
              }))}
              columns={associatedListColumns}
              {...other}
            />
          )}
        </>
      )}

      {isAssociatedContracts && (
        <>
          {items.length > 0 && (
            <DetailsList
              items={items.map(item => ({
                ...item,
                key: `contract-${item.contractId}`,
                link: `/contracts/${item.contractAddress}`,
              }))}
              columns={associatedContractColumns}
              {...other}
            />
          )}
        </>
      )}
    </>
  );
};

DimensionListItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      comment: PropTypes.string,
      hierarchy: PropTypes.string,
      isList: PropTypes.bool,
    }),
  ),
  associatedLists: PropTypes.arrayOf(
    PropTypes.shape({
      listId: PropTypes.string,
      listType: PropTypes.string,
      listName: PropTypes.string,
      listDescription: PropTypes.string,
      metadata: {},
    }),
  ),
  listType: PropTypes.string,
  isAssociatedContracts: PropTypes.bool,
};

DimensionListItems.defaultProps = {
  items: [],
  associatedLists: [],
  listType: '',
  isAssociatedContracts: false,
};

export default DimensionListItems;
